/* global console, document, Office */

import { createClient } from "@supabase/supabase-js";
import { executeTool, readSnapshot } from "./executor.js";

// ── Supabase config (injected at build time via webpack DefinePlugin) ──────
const SUPABASE_URL = process.env.SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.SUPABASE_ANON_KEY;
const ADDIN_ENV = process.env.ADDIN_ENV; // "dev" | "pro"
const IS_DEV = ADDIN_ENV !== "pro";

let supabase = null;
let commandChannel = null;
let instanceCommandChannel = null;
let currentUserId = null;
let pairingChannel = null;
let pairingCode = null;

// Unique instance ID for this add-in session — used for multi-document routing.
const instanceId = crypto.randomUUID();
let _heartbeatInterval = null;

// ── Entry point ────────────────────────────────────────────────────────────
Office.onReady(async (info) => {
  console.log("[AIPilot Add-in] Office ready. Host:", info.host, "env:", ADDIN_ENV);

  if (!IS_DEV) {
    document.querySelectorAll(".debug-only").forEach((el) => {
      el.style.display = "none";
    });
  }

  const signOutBtn = document.getElementById("sign-out-btn");
  if (signOutBtn) {
    signOutBtn.addEventListener("click", async () => {
      signOutBtn.disabled = true;
      signOutBtn.textContent = "Cerrando...";
      // NOTE: we intentionally DO NOT call supabase.auth.signOut(). That puts
      // the JS client in a "signed out" state that rejects setSession() on the
      // same page load with "Auth session missing!". A full reload re-creates
      // the client from scratch, so clearing storage + reload is enough.
      try { await OfficeRuntime.storage.removeItem(SESSION_KEY); } catch (_) { /* ignore */ }
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith("sb-") || k === SESSION_KEY)
          .forEach((k) => localStorage.removeItem(k));
      } catch (_) { /* ignore */ }
      try { sessionStorage.setItem("aipilot_force_sso", "1"); } catch (_) { /* ignore */ }
      signOutBtn.textContent = "Recargando...";
      setTimeout(() => location.reload(), 300);
    });
  }

  try {
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

    // If user just hit "Sign out", force SSO and skip the cache restore.
    let forceSSO = false;
    try {
      if (sessionStorage.getItem("aipilot_force_sso") === "1") {
        forceSSO = true;
        sessionStorage.removeItem("aipilot_force_sso");
      }
    } catch (_) { /* ignore */ }

    // 1. Try to restore a previously saved session
    const savedSession = forceSSO ? null : await loadSavedSession();
    if (savedSession) {
      const { data, error } = await supabase.auth.setSession(savedSession);
      if (!error && data.session?.user) {
        _lastSSOSnapshot = {
          source: "cached-session",
          msPayload: null,
          backendEmail: null,
          sessionEmail: data.session.user.email,
        };
        await onAuthenticated(data.session.user, info.host);
        return;
      }
    }

    // 2. Try SSO via Microsoft Entra (Office.auth.getAccessToken)
    updateStatus("Iniciando sesión automáticamente...");
    const ssoResult = await trySSOLogin(info.host);
    if (ssoResult) return;

    // 3. Fallback — show pairing code flow
    startPairingFlow(info.host);

  } catch (err) {
    console.error("[AIPilot Add-in] Init error:", err);
    updateStatus("Error al inicializar. Recarga el panel.");
  }
});

// ── SSO via Microsoft Entra ────────────────────────────────────────────────

function debugSSO(msg) {
  const el = document.getElementById("sso-debug");
  if (el) el.textContent = msg;
  console.warn("[SSO Debug]", msg);
}

function decodeJwtPayload(token) {
  try {
    const part = token.split(".")[1];
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=");
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

// Latest SSO diagnostic snapshot — rendered in the "Connected" view.
let _lastSSOSnapshot = null;

function renderSSOSnapshot() {
  const targets = [
    document.getElementById("sso-debug-panel"),
    document.getElementById("sso-debug-panel-pairing"),
  ].filter(Boolean);
  if (targets.length === 0) return;
  let text;
  if (!_lastSSOSnapshot) {
    text = "Sin datos de SSO (sesión restaurada de cache).";
  } else {
    const { msPayload, backendEmail, sessionEmail, source } = _lastSSOSnapshot;
    const lines = [
      `source: ${source}`,
      `Office SSO dice: ${msPayload?.preferred_username ?? msPayload?.upn ?? msPayload?.email ?? "?"}`,
      `Backend mapeó a: ${backendEmail ?? "?"}`,
      `Session Supabase: ${sessionEmail ?? "?"}`,
      `MS token oid: ${msPayload?.oid ?? "?"}`,
      `MS token tid: ${msPayload?.tid ?? "?"}`,
      `MS token appid: ${msPayload?.appid ?? "?"}`,
      `MS token aud: ${Array.isArray(msPayload?.aud) ? msPayload.aud.join(", ") : (msPayload?.aud ?? "?")}`,
      "",
      "--- full payload ---",
      JSON.stringify(msPayload, null, 2),
    ];
    text = lines.join("\n");
  }
  targets.forEach((el) => { el.textContent = text; });
}

async function trySSOLogin(host) {
  try {
    updateStatus("SSO: obteniendo token de Office...");
    let msToken;
    try {
      msToken = await Office.auth.getAccessToken({
        allowSignInPrompt: false,
        allowConsentPrompt: false,
        forMSGraphAccess: false,
      });
    } catch (e) {
      // Surface Office SSO error (code + message) so we can diagnose in-panel
      const ssoErr = `Office.auth error: code=${e?.code ?? "?"} msg=${e?.message ?? e}`;
      debugSSO(ssoErr);
      _lastSSOSnapshot = { source: "sso-failed", msPayload: null, backendEmail: null, sessionEmail: ssoErr };
      console.error("[AIPilot Add-in]", ssoErr, e);
      return false;
    }

    if (!msToken) {
      debugSSO("SSO: token vacío");
      _lastSSOSnapshot = { source: "sso-failed", msPayload: null, backendEmail: null, sessionEmail: "token vacío" };
      return false;
    }

    // Decode MS token locally (no validation — just to see what email Office sent)
    const msPayload = decodeJwtPayload(msToken);
    const msEmail = msPayload?.preferred_username ?? msPayload?.upn ?? msPayload?.email ?? "?";
    debugSSO(`SSO → Office dice: ${msEmail}`);
    console.log("[AIPilot Add-in] MS token payload:", msPayload);

    const res = await fetch(`${SUPABASE_URL}/functions/v1/office-auth`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ msToken }),
    });

    const result = await res.json();
    if (!result.success) {
      debugSSO(`SSO error: ${result.error}`);
      return false;
    }

    debugSSO(`SSO → Backend mapeó a: ${result.email ?? "?"} (MS dijo: ${msEmail})`);

    const { data, error } = await supabase.auth.setSession({
      access_token: result.accessToken,
      refresh_token: result.refreshToken,
    });

    if (error || !data.session?.user) {
      debugSSO(`SSO sesión fallida: ${error?.message ?? "sin sesión"}`);
      return false;
    }

    _lastSSOSnapshot = {
      source: "fresh-sso",
      msPayload,
      backendEmail: result.email,
      sessionEmail: data.session.user.email,
    };

    await saveSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    });

    await onAuthenticated(data.session.user, host);
    return true;

  } catch (err) {
    debugSSO(`SSO excepción: ${err.message ?? err}`);
    return false;
  }
}

// ── Pairing flow ───────────────────────────────────────────────────────────

function startPairingFlow(host) {
  pairingCode = generatePairingCode();
  console.log("[AIPilot Add-in] Pairing code:", pairingCode);

  showPairing(pairingCode);

  // Announce the pairing code so AIPilot apps can auto-pair.
  announcePairingCode(pairingCode);

  // Listen on the pairing channel for a token from AIPilot web
  pairingChannel = supabase
    .channel(`office-pair:${pairingCode}`, { config: { broadcast: { self: false } } })
    .on("broadcast", { event: "token" }, async ({ payload }) => {
      debugSSO(`Pairing: token recibido ✓ (procesando...)`);
      console.log("[AIPilot Add-in] Token received via pairing", payload ? Object.keys(payload) : null);

      try {
        if (!payload?.access_token || !payload?.refresh_token) {
          debugSSO(`Pairing error: payload inválido (keys: ${payload ? Object.keys(payload).join(",") : "null"})`);
          return;
        }

        let { data, error } = await supabase.auth.setSession({
          access_token: payload.access_token,
          refresh_token: payload.refresh_token,
        });

        // If the client is in a "signed out" state from a prior signOut call,
        // setSession rejects with "Auth session missing!". Recreate the client
        // and retry once.
        if (error && /auth session missing/i.test(error.message || "")) {
          debugSSO(`Pairing: recreando cliente tras signOut previo`);
          supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
          const retry = await supabase.auth.setSession({
            access_token: payload.access_token,
            refresh_token: payload.refresh_token,
          });
          data = retry.data;
          error = retry.error;
        }

        if (error || !data.session?.user) {
          debugSSO(`Pairing setSession error: ${error?.message ?? "sin sesión"}`);
          console.error("[AIPilot Add-in] Session error:", error);
          return;
        }

        await saveSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (pairingChannel) {
          pairingChannel.unsubscribe();
          pairingChannel = null;
        }
        stopAnnouncing();

        debugSSO(`Pairing: sesión creada para ${data.session.user.email}`);
        await onAuthenticated(data.session.user, host);
      } catch (e) {
        debugSSO(`Pairing excepción: ${e.message ?? e}`);
        console.error("[AIPilot Add-in] Pairing handler exception:", e);
      }
    })
    .subscribe((status, err) => {
      debugSSO(`Pairing channel: ${status}${err ? " · " + (err.message || err) : ""}`);
      console.log("[AIPilot Add-in] Pairing channel status:", status, err);
    });
}

// ── Called once we have a valid authenticated user ─────────────────────────
async function onAuthenticated(user, host) {
  currentUserId = user.id;
  console.log("[AIPilot Add-in] Authenticated as:", user.email, "instanceId:", instanceId);

  await publishContext(host);
  await registerInstance(host);
  subscribeToCommands(host);
  startHeartbeat();

  // Unregister on page close
  window.addEventListener("beforeunload", () => {
    fetch(`${SUPABASE_URL}/functions/v1/office-register-instance`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
      body: JSON.stringify({ action: "unregister", instanceId }),
      keepalive: true,
    });
  });

  showConnected(user.email);
  renderSSOSnapshot();
}

// ── Instance registration for multi-document routing ─────────────────────

async function registerInstance(host) {
  const documentUrl = Office.context.document?.url ?? "";
  const documentName = documentUrl.split("/").pop()?.split("?")[0] ?? "Documento sin nombre";

  try {
    await fetch(`${SUPABASE_URL}/functions/v1/office-register-instance`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
      body: JSON.stringify({
        action: "register",
        instanceId,
        userId: currentUserId,
        host: host?.toString() ?? "unknown",
        documentName,
        documentUrl,
      }),
    });
    console.log("[AIPilot Add-in] Instance registered:", instanceId, documentName);
  } catch (err) {
    console.warn("[AIPilot Add-in] Could not register instance:", err);
  }
}

function startHeartbeat() {
  if (_heartbeatInterval) clearInterval(_heartbeatInterval);
  _heartbeatInterval = setInterval(async () => {
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/office-register-instance`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({ action: "heartbeat", instanceId }),
      });
    } catch (_) { /* silent */ }
  }, 30000);
}

// ── Supabase Realtime: listen for commands from AIPilot ────────────────────

// Shared command handlers — used by both legacy and instance-specific channels.
function attachCommandHandlers(channel, host, label) {
  return channel
    .on("broadcast", { event: "get-snapshot" }, async ({ payload }) => {
      console.log(`[AIPilot Add-in] [${label}] Snapshot requested`);
      updateConnectionDebug(`get-snapshot via ${label} @ ${new Date().toISOString()}`, "lastCmd");
      updateStatusConnected("Leyendo documento...");
      const snapshot = await readSnapshot(host);
      const displayLanguage = Office.context.displayLanguage ?? "es-ES";
      await broadcastSnapshot(payload.requestId, snapshot, displayLanguage);
      updateStatusConnected("");
    })
    .on("broadcast", { event: "tool-call" }, async ({ payload }) => {
      const { toolCallId, toolName, input } = payload;
      console.log(`[AIPilot Add-in] [${label}] Tool call:`, toolName, toolCallId);
      updateConnectionDebug(`tool-call ${toolName} via ${label}`, "lastCmd");
      updateStatusConnected(`⚙️ ${toolName}...`);

      const t0 = Date.now();
      const result = await executeTool(toolName, input, host);
      result.durationMs = Date.now() - t0;

      console.log("[AIPilot Add-in] Tool result:", result);
      await broadcastToolResult(toolCallId, result);

      updateStatusConnected(result.success ? `✓ ${toolName}` : `✗ ${result.error}`);
      setTimeout(() => updateStatusConnected(""), 3000);
    });
}

function subscribeToCommands(host) {
  if (commandChannel) commandChannel.unsubscribe();
  if (instanceCommandChannel) instanceCommandChannel.unsubscribe();

  const hostKey = host?.toString().toLowerCase().replace("powerpoint", "powerpoint") ?? "excel";

  // 1. Legacy channel: office-commands:{userId}:{appName} (backward compat)
  const legacyName = `office-commands:${currentUserId}:${hostKey}`;
  console.log("[AIPilot Add-in] Subscribing to legacy channel:", legacyName);
  commandChannel = attachCommandHandlers(supabase.channel(legacyName), host, "legacy")
    .subscribe((status, err) => {
      console.log("[AIPilot Add-in] Legacy channel:", status, err);
      updateConnectionDebug(`legacy: ${status}${err ? " · " + (err.message || err) : ""}`, "legacy");
    });

  // 2. Instance-specific channel: office-commands:{userId}:{instanceId}
  const instanceName = `office-commands:${currentUserId}:${instanceId}`;
  console.log("[AIPilot Add-in] Subscribing to instance channel:", instanceName);
  instanceCommandChannel = attachCommandHandlers(supabase.channel(instanceName), host, "instance")
    .subscribe((status, err) => {
      console.log("[AIPilot Add-in] Instance channel:", status, err);
      updateConnectionDebug(`instance: ${status}${err ? " · " + (err.message || err) : ""}`, "instance");
    });
}

const _connectionDebug = { legacy: "?", instance: "?", lastCmd: "-" };
function updateConnectionDebug(line, key) {
  if (key) _connectionDebug[key] = line;
  const el = document.getElementById("conn-debug");
  if (el) {
    el.textContent = [
      `Legacy: ${_connectionDebug.legacy}`,
      `Instance: ${_connectionDebug.instance}`,
      `Último comando: ${_connectionDebug.lastCmd}`,
    ].join("\n");
  }
}

// ── Broadcast snapshot back to AIPilot ────────────────────────────────────
async function broadcastSnapshot(requestId, snapshot, displayLanguage) {
  try {
    const session = await supabase.auth.getSession();
    const accessToken = session?.data?.session?.access_token;

    await fetch(`${SUPABASE_URL}/realtime/v1/api/broadcast`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        messages: [{
          topic: `office-results:${currentUserId}`,
          event: "snapshot",
          payload: { requestId, snapshot, displayLanguage, instanceId },
        }],
      }),
    });
  } catch (err) {
    console.error("[AIPilot Add-in] Failed to broadcast snapshot:", err);
  }
}

// ── Broadcast tool result back to Flutter ─────────────────────────────────
async function broadcastToolResult(toolCallId, result) {
  try {
    const session = await supabase.auth.getSession();
    const accessToken = session?.data?.session?.access_token;
    await fetch(`${SUPABASE_URL}/realtime/v1/api/broadcast`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        messages: [{
          topic: `office-results:${currentUserId}`,
          event: "tool-result",
          payload: {
            toolCallId,
            instanceId,
            success: result.success,
            data: result.data ?? null,
            error: result.error ?? null,
          },
        }],
      }),
    });
  } catch (err) {
    console.error("[AIPilot Add-in] Failed to broadcast tool result:", err);
  }
}

// ── Send result via REST API (reliable, no channel state dependency) ────────
async function broadcastResult(commandId, result) {
  try {
    const session = await supabase.auth.getSession();
    const accessToken = session?.data?.session?.access_token;

    const res = await fetch(`${SUPABASE_URL}/realtime/v1/api/broadcast`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        messages: [
          {
            topic: `office-results:${currentUserId}`,
            event: "result",
            payload: {
              commandId,
              success: result.success,
              message: result.message,
              data: result.data ?? null,
              error: result.error ?? null,
            },
          },
        ],
      }),
    });
    console.log("[AIPilot Add-in] Broadcast result HTTP status:", res.status);
  } catch (err) {
    console.error("[AIPilot Add-in] Failed to broadcast result:", err);
  }
}

// ── Announce pairing code so AIPilot apps can auto-pair ──────────────────
// Calls Edge Function to insert code into DB. Flutter watches the table.
// Shows status in the SSO debug element so we can diagnose issues.
let _announceInterval = null;

function announcePairingCode(code) {
  let attempts = 0;

  async function sendAnnounce() {
    attempts++;
    try {
      debugSSO(`Auto-pair: enviando código (intento ${attempts})...`);
      const res = await fetch(`${SUPABASE_URL}/functions/v1/office-pair-announce`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.success) {
        debugSSO(`Auto-pair: código enviado ✓`);
      } else {
        debugSSO(`Auto-pair: error ${data.error}`);
      }
    } catch (err) {
      debugSSO(`Auto-pair: fetch error — ${err.message || err}`);
    }
  }

  sendAnnounce();
  _announceInterval = setInterval(sendAnnounce, 5000);
  setTimeout(stopAnnouncing, 120000);
}

function stopAnnouncing() {
  if (_announceInterval) {
    clearInterval(_announceInterval);
    _announceInterval = null;
  }
}

// ── Publish document context to AIPilot ───────────────────────────────────
async function publishContext(host) {
  try {
    const documentUrl = Office.context.document?.url ?? "";
    const documentName = documentUrl.split("/").pop()?.split("?")[0] ?? "";
    const officeVersion = Office.context.diagnostics?.version ?? "unknown";
    const displayLanguage = Office.context.displayLanguage ?? "en-US";
    const isReadOnly = Office.context.document?.mode === Office.DocumentMode.ReadOnly;

    await supabase.channel(`office-context:${currentUserId}`).send({
      type: "broadcast",
      event: "context",
      payload: {
        host: host?.toString() ?? "unknown",
        documentUrl,
        documentName,
        officeVersion,
        displayLanguage,
        isReadOnly,
        connectedAt: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.warn("[AIPilot Add-in] Could not publish context:", err);
  }
}

// ── Session persistence (OfficeRuntime.storage) ────────────────────────────

const SESSION_KEY = "aipilot_addin_session";

async function saveSession(tokens) {
  try {
    await OfficeRuntime.storage.setItem(SESSION_KEY, JSON.stringify(tokens));
  } catch (_) {
    // OfficeRuntime.storage not available in all hosts — ignore
  }
}

async function loadSavedSession() {
  try {
    const raw = await OfficeRuntime.storage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

async function clearSavedSession() {
  try {
    await OfficeRuntime.storage.removeItem(SESSION_KEY);
  } catch (_) { /* ignore */ }
  try {
    await supabase?.auth.signOut();
  } catch (_) { /* ignore */ }
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("sb-") || k === SESSION_KEY)
      .forEach((k) => localStorage.removeItem(k));
  } catch (_) { /* ignore */ }
}

// ── Pairing code generator ─────────────────────────────────────────────────

function generatePairingCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0/O/1/I to avoid confusion
  let code = "";
  for (let i = 0; i < 6; i++) {
    if (i === 3) code += "-";
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code; // e.g. "ABC-123"
}

// ── UI helpers ─────────────────────────────────────────────────────────────

function updateStatus(text) {
  const el = document.getElementById("status");
  if (el) el.textContent = text;
}

function updateStatusConnected(text) {
  const el = document.getElementById("status-connected");
  if (el) el.textContent = text;
}

function showPairing(code) {
  document.getElementById("view-connecting").style.display = "none";
  document.getElementById("view-pairing").style.display = "flex";
  document.getElementById("view-connected").style.display = "none";
  document.getElementById("pairing-code").textContent = code;
  renderSSOSnapshot();
}

function showConnected(email) {
  document.getElementById("view-connecting").style.display = "none";
  document.getElementById("view-pairing").style.display = "none";
  document.getElementById("view-connected").style.display = "flex";
  document.getElementById("user-email").textContent = email;
}
